import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useShop } from './ShopContext';
import { slideApiService } from '../services/slideApiService';

/**
 * Tracks the authenticated user's favorited slide IDs for the current shop.
 * Favorites are a transient working set backed by Redis on slide-svc. Shared via
 * context so slide cards and the detail modal stay in sync. Mirrors the iOS
 * FavoritesManager.
 */
interface FavoritesContextType {
  favoriteIds: Set<string>;
  isFavorite: (slideId: string) => boolean;
  toggleFavorite: (slideId: string) => Promise<void>;
  refresh: () => Promise<void>;
  loading: boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentShop, isInternalMember } = useShop();
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Reload favorites from the server for the current shop. Used on shop change
  // and after operations that mutate the server-side set (e.g. save-as-collection
  // clears favorites in Redis).
  const refresh = useCallback(async () => {
    const shopId = currentShop?.id;
    // Favorites are a shop-internal working set; slide-svc's /favorites endpoints 403 external
    // (CLIENT/VENDOR) roles. External members never load or hold favorites, so skip the call
    // entirely (avoids a doomed 403 on every shared-slide view).
    if (!shopId || !isInternalMember) {
      setFavoriteIds(new Set());
      return;
    }
    try {
      setLoading(true);
      const response = await slideApiService.getFavorites(shopId);
      setFavoriteIds(new Set(response.slideIds));
    } catch (error) {
      console.error('Failed to load favorites:', error);
    } finally {
      setLoading(false);
    }
  }, [currentShop?.id, isInternalMember]);

  // Load favorites whenever the current shop changes.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const isFavorite = useCallback((slideId: string) => favoriteIds.has(slideId), [favoriteIds]);

  const toggleFavorite = useCallback(async (slideId: string) => {
    const shopId = currentShop?.id;
    if (!shopId) return;

    const wasFavorite = favoriteIds.has(slideId);

    // Optimistic update
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (wasFavorite) { next.delete(slideId); } else { next.add(slideId); }
      return next;
    });

    try {
      if (wasFavorite) {
        await slideApiService.unfavoriteSlide(shopId, slideId);
      } else {
        await slideApiService.favoriteSlide(shopId, slideId);
      }
    } catch (error) {
      // Roll back on failure
      console.error('Failed to toggle favorite:', error);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (wasFavorite) { next.add(slideId); } else { next.delete(slideId); }
        return next;
      });
    }
  }, [currentShop?.id, favoriteIds]);

  return (
    <FavoritesContext.Provider value={{ favoriteIds, isFavorite, toggleFavorite, refresh, loading }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
